import React, { useEffect, useMemo, useRef, useState } from 'react'
import { formatDate } from '@/utils/dateFormat'
import { getDocuments, uploadDocument, fetchDocumentBlob } from '@/api/tapResources'
import { TapDocument } from '@/types/index'
import { Upload, FileText, Eye, Download } from 'lucide-react'

// Feature #161 — Documents tab with drag-and-drop upload + inline preview.
// Drag-and-drop is an explicit PO requirement from the work item.
interface DocumentsTabProps {
  applicationId: string
}

// Document kinds per Requirements §3.10.
const DOCUMENT_KINDS = ['Resume', 'CoverLetter', 'Certificate', 'Portfolio', 'ReferenceLetter', 'Other']

// What we allow reviewers to upload. The backend stores raw bytes, but
// stopping executables / archives at the client is a low-effort guardrail.
const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt', '.rtf', '.png', '.jpg', '.jpeg']
const MAX_FILE_SIZE = 25 * 1024 * 1024 // 25 MB

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '12px',
}

const primaryBtn: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-magenta)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}
const secondaryBtn: React.CSSProperties = {
  ...primaryBtn,
  backgroundColor: 'var(--color-white)',
  color: 'var(--color-black)',
  border: '1px solid var(--color-light-grey)',
}

const formatSize = (bytes?: number): string => {
  if (bytes == null) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

interface UploadRow {
  filename: string
  state: 'queued' | 'uploading' | 'done' | 'error'
  message?: string
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({ applicationId }) => {
  const [documents, setDocuments] = useState<TapDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [uploads, setUploads] = useState<UploadRow[]>([])
  const [previewing, setPreviewing] = useState<TapDocument | null>(null)
  // Bug #204: resolved blob URL for preview / open (for local-fallback docs
  // the raw storageUrl can't be used directly in an iframe or new tab).
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null)
  const [openingDoc, setOpeningDoc] = useState<string | null>(null) // doc id while loading
  const [selectedKind, setSelectedKind] = useState('Resume')
  const inputRef = useRef<HTMLInputElement | null>(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await getDocuments(applicationId)
      // Group latest versions first per filename.
      setDocuments([...result].sort((a, b) =>
        a.filename === b.filename
          ? b.version - a.version
          : a.filename.localeCompare(b.filename)
      ))
    } catch {
      setError('Unable to load documents')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId])

  // Latest-version-first list, plus a map of older versions for the history
  // chevron we render beneath the current version.
  const grouped = useMemo(() => {
    const byFilename = new Map<string, TapDocument[]>()
    documents.forEach((d) => {
      const list = byFilename.get(d.filename) || []
      list.push(d)
      byFilename.set(d.filename, list)
    })
    return Array.from(byFilename.values()).map((versions) =>
      [...versions].sort((a, b) => b.version - a.version)
    )
  }, [documents])

  const validate = (file: File): string | null => {
    const ext = '.' + (file.name.split('.').pop() || '').toLowerCase()
    if (!ALLOWED_EXTENSIONS.includes(ext)) return `Unsupported file type: ${ext}`
    if (file.size > MAX_FILE_SIZE) return `File too large (${formatSize(file.size)})`
    return null
  }

  const uploadFile = async (file: File, index: number) => {
    setUploads((prev) => prev.map((u, i) => i === index ? { ...u, state: 'uploading' } : u))
    try {
      // Bug #176: upload the actual file bytes to Azure Blob Storage via the
      // backend's /upload endpoint. The backend stores the file and returns a
      // document record with a real storage URL.
      await uploadDocument(applicationId, file, selectedKind)
      setUploads((prev) => prev.map((u, i) => i === index ? { ...u, state: 'done' } : u))
    } catch {
      setUploads((prev) => prev.map((u, i) => i === index ? { ...u, state: 'error', message: 'Upload failed' } : u))
    }
  }

  const handleFiles = async (files: FileList | File[]) => {
    const list = Array.from(files)
    const initial: UploadRow[] = list.map((f) => {
      const err = validate(f)
      return err
        ? { filename: f.name, state: 'error', message: err }
        : { filename: f.name, state: 'queued' }
    })
    const startIdx = uploads.length
    setUploads((prev) => [...prev, ...initial])
    for (let i = 0; i < list.length; i++) {
      if (initial[i].state === 'error') continue
      await uploadFile(list[i], startIdx + i)
    }
    await load()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files)
    }
  }

  // Bug #211: always proxy through the backend's /content endpoint so users
  // without Azure AD blob access can view documents.
  const handleOpen = async (doc: TapDocument) => {
    if (isPending(doc)) return
    setOpeningDoc(doc.id)
    try {
      const url = await fetchDocumentBlob(doc.storageUrl, applicationId, doc.id)
      window.open(url, '_blank')
    } catch {
      alert('Failed to open document')
    } finally {
      setOpeningDoc(null)
    }
  }

  // Bug #211: preview also goes through the proxy endpoint.
  const handlePreview = async (doc: TapDocument) => {
    if (isPending(doc)) return
    setPreviewing(doc)
    setPreviewBlobUrl(null)
    try {
      const url = await fetchDocumentBlob(doc.storageUrl, applicationId, doc.id)
      setPreviewBlobUrl(url)
    } catch {
      // Will show an error state in the preview overlay
    }
  }

  // Clean up blob URLs on preview close to avoid memory leaks.
  const handleClosePreview = () => {
    if (previewBlobUrl?.startsWith('blob:')) URL.revokeObjectURL(previewBlobUrl)
    setPreviewing(null)
    setPreviewBlobUrl(null)
  }

  // Bug #175: documents with a `pending://` storage URL haven't actually been
  // uploaded to blob storage yet (the presigned-upload flow is tracked
  // separately). Attempting to open or preview these URLs causes the browser to
  // fail with a "scheme does not have a registered handler" error, or silently
  // opens an empty tab. We guard against this everywhere the URL is used.
  const isPending = (d: TapDocument): boolean =>
    !d.storageUrl || d.storageUrl.startsWith('pending://')

  const isPreviewable = (d: TapDocument): boolean => {
    if (isPending(d)) return false
    const name = d.filename.toLowerCase()
    return name.endsWith('.pdf') || (d.contentType || '').includes('pdf')
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Documents</h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <label style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>
            Uploading as:
            <select
              value={selectedKind}
              onChange={(e) => setSelectedKind(e.target.value)}
              style={{
                marginLeft: '6px',
                padding: '4px 8px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '4px',
                fontSize: '12px',
                fontFamily: 'var(--font-family)',
                cursor: 'pointer',
              }}
            >
              {DOCUMENT_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>
        </div>
      </div>

      {/* Drag-and-drop zone — explicit PO requirement */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? 'var(--color-magenta)' : 'var(--color-light-grey)'}`,
          borderRadius: '10px',
          padding: '32px',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: dragOver ? '#fdf2f8' : 'var(--color-white)',
          marginBottom: '16px',
          transition: 'border-color 120ms ease',
        }}
      >
        <Upload size={28} style={{ color: dragOver ? 'var(--color-magenta)' : 'var(--color-dark-grey)', marginBottom: '8px' }} />
        <div style={{ fontSize: '14px', color: 'var(--color-black)', marginBottom: '4px' }}>
          Drop files here, or click to select
        </div>
        <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>
          Accepts PDF, Word, plain text, and images up to 25 MB.
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files) handleFiles(e.target.files)
            e.target.value = ''
          }}
        />
      </div>

      {uploads.length > 0 && (
        <div style={cardStyle}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Recent uploads</div>
          {uploads.map((u, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '4px 0' }}>
              <span>{u.filename}</span>
              <span style={{
                color: u.state === 'error' ? 'var(--color-magenta)'
                  : u.state === 'done' ? '#16a34a'
                    : 'var(--color-dark-grey)',
              }}>
                {u.state === 'queued' && 'Queued'}
                {u.state === 'uploading' && 'Uploading…'}
                {u.state === 'done' && 'Done'}
                {u.state === 'error' && (u.message || 'Failed')}
              </span>
            </div>
          ))}
        </div>
      )}

      {loading && <div style={{ color: 'var(--color-dark-grey)' }}>Loading documents…</div>}
      {error && <div style={{ color: 'var(--color-magenta)' }}>{error}</div>}

      {!loading && grouped.length === 0 && (
        <div style={cardStyle}>
          <div style={{ color: 'var(--color-dark-grey)' }}>No documents uploaded yet.</div>
        </div>
      )}

      {grouped.map((versions) => {
        const latest = versions[0]
        const older = versions.slice(1)
        return (
          <div key={latest.filename} style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <FileText size={18} style={{ color: 'var(--color-dark-grey)' }} />
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 500 }}>{latest.filename}</div>
                  <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>
                    {latest.kind} · v{latest.version} · {formatSize(latest.sizeBytes)}
                    {latest.uploadedAt && ` · ${formatDate(latest.uploadedAt)}`}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                {isPreviewable(latest) && (
                  <button onClick={() => handlePreview(latest)} style={secondaryBtn}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Eye size={13} /> Preview
                    </span>
                  </button>
                )}
                {/* Bug #175: disable Open for pending uploads.
                    Bug #204: use authenticated fetch instead of direct <a href>
                    so local-fallback URLs resolve to the backend with the Bearer
                    token attached. */}
                {isPending(latest) ? (
                  <span
                    style={{
                      ...secondaryBtn,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      opacity: 0.5,
                      cursor: 'not-allowed',
                    }}
                    title="File is still being processed"
                  >
                    <Download size={13} /> Pending
                  </span>
                ) : (
                  <button
                    onClick={() => handleOpen(latest)}
                    disabled={openingDoc === latest.id}
                    style={{ ...secondaryBtn, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                  >
                    <Download size={13} /> {openingDoc === latest.id ? 'Opening…' : 'Open'}
                  </button>
                )}
              </div>
            </div>
            {older.length > 0 && (
              <details style={{ marginTop: '10px' }}>
                <summary style={{ cursor: 'pointer', fontSize: '12px', color: 'var(--color-dark-grey)' }}>
                  {older.length} earlier version{older.length === 1 ? '' : 's'}
                </summary>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '16px', fontSize: '12px', color: 'var(--color-dark-grey)' }}>
                  {older.map((v) => (
                    <li key={v.id}>
                      v{v.version} · {formatSize(v.sizeBytes)}
                      {v.uploadedAt && ` · ${formatDate(v.uploadedAt)}`}
                      {' · '}
                      {/* Bug #175: guard older-version links against pending URLs.
                          Bug #204: use authenticated fetch for local-fallback URLs. */}
                      {isPending(v)
                        ? <span style={{ opacity: 0.5 }}>pending</span>
                        : <button
                            onClick={() => handleOpen(v)}
                            style={{ background: 'none', border: 'none', color: 'var(--color-magenta)', cursor: 'pointer', padding: 0, fontSize: 'inherit', fontFamily: 'inherit' }}
                          >open</button>}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )
      })}

      {/* Bug #175: only render the preview overlay when the URL is real.
          Bug #204: use the resolved blob URL (fetched through the authenticated
          client) so local-fallback documents display correctly. */}
      {previewing && !isPending(previewing) && (
        <div
          onClick={handleClosePreview}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--color-white)',
              borderRadius: '10px',
              width: '100%',
              maxWidth: '1000px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--color-light-grey)' }}>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>{previewing.filename}</div>
              <button onClick={handleClosePreview} style={secondaryBtn}>Close</button>
            </div>
            {previewBlobUrl ? (
              <iframe
                src={previewBlobUrl}
                title={previewing.filename}
                style={{ flex: 1, border: 'none', width: '100%' }}
              />
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-dark-grey)' }}>
                Loading preview…
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default DocumentsTab
